'use client'

import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { AlertCircle, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/patterns/form-field'
import { loginSchema, type LoginInput } from '@/features/auth/schemas'
import { useLogin } from '@/features/auth/hooks/use-login'

export function LoginForm() {
  const { mutate: login, isPending, isError, error } = useLogin()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  })

  function onSubmit(data: LoginInput) {
    login(data)
  }

  return (
    <Card className="shadow-sm border border-[var(--color-border-custom)]">
      <CardHeader>
        <CardTitle className="text-2xl">Entrar</CardTitle>
        <CardDescription>Acesse sua conta para gerenciar suas cidades</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="flex flex-col gap-4">
          {isError && (
            <div className="flex items-center gap-2 rounded-lg bg-[var(--color-error-subtle)] p-3 text-sm text-[var(--color-error)]">
              <AlertCircle className="size-4 shrink-0" />
              <span>{error?.message || 'Email ou senha incorretos'}</span>
            </div>
          )}

          <FormField label="Email" htmlFor="email" error={errors.email?.message} required>
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              autoComplete="email"
              {...register('email')}
            />
          </FormField>

          <FormField label="Senha" htmlFor="password" error={errors.password?.message} required>
            <Input
              id="password"
              type="password"
              placeholder="Sua senha"
              autoComplete="current-password"
              {...register('password')}
            />
          </FormField>

          <div className="flex justify-end">
            <Link
              href="/forgot-password"
              className="text-sm text-[var(--color-primary-brand)] hover:underline"
            >
              Esqueceu a senha?
            </Link>
          </div>

          <Button
            type="submit"
            disabled={isPending}
            className="w-full text-white font-semibold"
          >
            {isPending && <Loader2 className="animate-spin" />}
            Entrar
          </Button>
        </CardContent>

        <CardFooter className="justify-center">
          <p className="text-sm text-[var(--color-text-muted)]">
            Não tem conta?{' '}
            <Link href="/register" className="font-medium text-[var(--color-primary-brand)] hover:underline">
              Criar conta
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  )
}
